import * as fflate from "fflate";

const TAG_DICT = {
  "0002,0010": "Transfer Syntax UID",
  "0008,0016": "SOP Class UID",
  "0008,0018": "SOP Instance UID",
  "0008,0020": "Study Date",
  "0008,0030": "Study Time",
  "0008,0050": "Accession Number",
  "0008,0060": "Modality",
  "0008,0070": "Manufacturer",
  "0008,0080": "Institution Name",
  "0008,1030": "Study Description",
  "0008,103E": "Series Description",
  "0010,0010": "Patient Name",
  "0010,0020": "Patient ID",
  "0010,0030": "Patient Birth Date",
  "0010,0040": "Patient Sex",
  "0010,1010": "Patient Age",
  "0018,0015": "Body Part Examined",
  "0018,0050": "Slice Thickness",
  "0018,1000": "Device Serial Number",
  "0018,1020": "Software Versions",
  "0018,1030": "Protocol Name",
  "0018,1150": "Exposure Time",
  "0018,1151": "X-Ray Tube Current",
  "0018,1152": "Exposure",
  "0018,5100": "Patient Position",
  "0020,000D": "Study Instance UID",
  "0020,000E": "Series Instance UID",
  "0020,0010": "Study ID",
  "0020,0011": "Series Number",
  "0020,0013": "Instance Number",
  "0020,0032": "Image Position (Patient)",
  "0020,0037": "Image Orientation (Patient)",
  "0020,1041": "Slice Location",
  "0028,0002": "Samples per Pixel",
  "0028,0004": "Photometric Interpretation",
  "0028,0008": "Number of Frames",
  "0028,0010": "Rows",
  "0028,0011": "Columns",
  "0028,0030": "Pixel Spacing",
  "0028,0100": "Bits Allocated",
  "0028,0101": "Bits Stored",
  "0028,0102": "High Bit",
  "0028,0103": "Pixel Representation",
  "0028,1050": "Window Center",
  "0028,1051": "Window Width",
  "0028,1052": "Rescale Intercept",
  "0028,1053": "Rescale Slope",
  "0028,1054": "Rescale Type",
  "7FE0,0010": "Pixel Data",
};

export const formatTag = (group, element) => {
  const g = group.toString(16).padStart(4, "0").toUpperCase();
  const e = element.toString(16).padStart(4, "0").toUpperCase();
  return `${g},${e}`;
};

const decodeString = (bytes) => {
  let str = "";
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b === 0) break;
    str += String.fromCharCode(b);
  }
  return str.trim();
};

export const parseDicom = (inputBuffer) => {
  const uint8 = inputBuffer instanceof Uint8Array ? inputBuffer : new Uint8Array(inputBuffer);
  const dataView = new DataView(uint8.buffer, uint8.byteOffset, uint8.byteLength);

  let pos = 0;
  if (uint8.length >= 132) {
    const magic = String.fromCharCode(uint8[128], uint8[129], uint8[130], uint8[131]);
    if (magic === "DICM") {
      pos = 132;
    }
  }

  const tags = {};
  const allElements = [];
  let pixelDataInfo = null;

  while (pos + 4 <= uint8.length) {
    const group = dataView.getUint16(pos, true);
    const element = dataView.getUint16(pos + 2, true);
    pos += 4;

    if (group === 0xFFFE) {
      if (pos + 4 <= uint8.length) {
        const itemLen = dataView.getUint32(pos, true);
        pos += 4;
        if (itemLen !== 0xFFFFFFFF) pos += itemLen;
      }
      continue;
    }

    let vr = "";
    let len = 0;
    let headerSize = 0;

    const c1 = uint8[pos];
    const c2 = uint8[pos + 1];
    const isAsciiVR = c1 >= 65 && c1 <= 90 && c2 >= 65 && c2 <= 90;

    if (group === 0x0002 || isAsciiVR) {
      vr = String.fromCharCode(c1, c2);
      if (["OB", "OW", "OF", "OD", "SQ", "UT", "UN", "UC", "UR"].includes(vr)) {
        if (pos + 8 > uint8.length) break;
        len = dataView.getUint32(pos + 4, true);
        headerSize = 8;
      } else {
        if (pos + 4 > uint8.length) break;
        len = dataView.getUint16(pos + 2, true);
        headerSize = 4;
      }
    } else {
      vr = "UN";
      if (pos + 4 > uint8.length) break;
      len = dataView.getUint32(pos, true);
      headerSize = 4;
    }

    pos += headerSize;

    const tagKey = formatTag(group, element);

    if (group === 0x7FE0 && element === 0x0010) {
      pixelDataInfo = {
        offset: pos,
        length: len,
        vr,
      };
      allElements.push({
        tag: tagKey,
        name: TAG_DICT[tagKey] || "Pixel Data",
        vr,
        length: len,
        value: "[Pixel Data Binary]",
      });
      break;
    }

    if (len === 0xFFFFFFFF) {
      break;
    }

    if (pos + len > uint8.length) {
      len = uint8.length - pos;
    }

    let val = null;
    const valBytes = uint8.subarray(pos, pos + len);

    try {
      if (["CS", "SH", "LO", "ST", "LT", "UT", "PN", "UI", "DA", "TM", "DT", "AS", "AE"].includes(vr)) {
        val = decodeString(valBytes);
      } else if (vr === "IS" || vr === "DS") {
        const s = decodeString(valBytes);
        if (s.includes("\\")) {
          val = s.split("\\").map((v) => Number(v.trim()));
        } else {
          val = s !== "" ? Number(s) : null;
        }
      } else if (vr === "US") {
        if (len >= 2) val = dataView.getUint16(pos, true);
      } else if (vr === "UL") {
        if (len >= 4) val = dataView.getUint32(pos, true);
      } else if (vr === "SS") {
        if (len >= 2) val = dataView.getInt16(pos, true);
      } else if (vr === "SL") {
        if (len >= 4) val = dataView.getInt32(pos, true);
      } else if (vr === "FL") {
        if (len >= 4) val = dataView.getFloat32(pos, true);
      } else if (vr === "FD") {
        if (len >= 8) val = dataView.getFloat64(pos, true);
      } else {
        val = decodeString(valBytes) || "[Binary Data]";
      }
    } catch {
      val = "[Unparsed]";
    }

    tags[tagKey] = val;
    allElements.push({
      tag: tagKey,
      name: TAG_DICT[tagKey] || "Private/Standard Tag",
      vr,
      length: len,
      value: val !== null && val !== undefined ? String(val) : "—",
    });

    pos += len;
  }

  const rows = tags["0028,0010"] || 0;
  const columns = tags["0028,0011"] || 0;
  const bitsAllocated = tags["0028,0100"] || 16;
  const bitsStored = tags["0028,0101"] || bitsAllocated;
  const pixelRepresentation = tags["0028,0103"] || 0;
  const samplesPerPixel = tags["0028,0002"] || 1;
  const photometricInterpretation = tags["0028,0004"] || "MONOCHROME2";

  let windowCenter = tags["0028,1050"];
  if (Array.isArray(windowCenter)) windowCenter = windowCenter[0];
  let windowWidth = tags["0028,1051"];
  if (Array.isArray(windowWidth)) windowWidth = windowWidth[0];

  const rescaleIntercept = tags["0028,1052"] != null ? Number(tags["0028,1052"]) : 0;
  const rescaleSlope = tags["0028,1053"] != null ? Number(tags["0028,1053"]) : 1;

  const patientName = tags["0010,0010"] ? String(tags["0010,0010"]).replace(/\^/g, " ") : "Unknown Patient";
  const patientId = tags["0010,0020"] || "—";
  const patientSex = tags["0010,0040"] || "—";
  const modality = tags["0008,0060"] || "OT";
  const studyDescription = tags["0008,1030"] || "Radiology Study";
  const seriesDescription = tags["0008,103E"] || "";
  const studyDate = tags["0008,0020"] || "";
  const instanceNumber = tags["0020,0013"] != null ? Number(tags["0020,0013"]) : 1;
  const sliceLocation = tags["0020,1041"] != null ? Number(tags["0020,1041"]) : null;

  let pixelArray = null;
  let minPixel = Infinity;
  let maxPixel = -Infinity;
  let isEncapsulated = false;
  let jpegBlobUrl = null;

  if (pixelDataInfo && rows > 0 && columns > 0) {
    const pixelOffset = pixelDataInfo.offset;
    const pixelLen = pixelDataInfo.length;

    if (pixelLen === 0xFFFFFFFF || pixelLen === 0) {
      isEncapsulated = true;
      for (let i = pixelOffset; i < uint8.length - 1; i++) {
        if (uint8[i] === 0xFF && uint8[i + 1] === 0xD8) {
          let end = uint8.length;
          for (let j = i + 2; j < uint8.length - 1; j++) {
            if (uint8[j] === 0xFF && uint8[j + 1] === 0xD9) {
              end = j + 2;
              break;
            }
          }
          const jpegSlice = uint8.subarray(i, end);
          const blob = new Blob([jpegSlice], { type: "image/jpeg" });
          jpegBlobUrl = URL.createObjectURL(blob);
          break;
        }
      }
    } else {
      const totalPixels = rows * columns;
      if (bitsAllocated === 16) {
        const numShorts = Math.min(totalPixels, Math.floor((uint8.length - pixelOffset) / 2));
        pixelArray = new Float32Array(numShorts);

        const isSigned = pixelRepresentation === 1;
        const mask = (1 << bitsStored) - 1;
        const signBit = 1 << (bitsStored - 1);

        for (let i = 0; i < numShorts; i++) {
          const byteIdx = pixelOffset + i * 2;
          let raw = dataView.getUint16(byteIdx, true);
          if (isSigned) {
            if ((raw & signBit) !== 0) {
              raw = -((~raw + 1) & mask);
            } else {
              raw = raw & mask;
            }
          } else {
            raw = raw & mask;
          }

          const val = raw * rescaleSlope + rescaleIntercept;
          pixelArray[i] = val;
          if (val < minPixel) minPixel = val;
          if (val > maxPixel) maxPixel = val;
        }
      } else if (bitsAllocated === 8) {
        const numBytes = Math.min(totalPixels * samplesPerPixel, uint8.length - pixelOffset);
        pixelArray = new Float32Array(numBytes);
        for (let i = 0; i < numBytes; i++) {
          const val = uint8[pixelOffset + i] * rescaleSlope + rescaleIntercept;
          pixelArray[i] = val;
          if (val < minPixel) minPixel = val;
          if (val > maxPixel) maxPixel = val;
        }
      }
    }
  }

  if (minPixel !== Infinity && maxPixel !== -Infinity) {
    if (windowWidth == null || windowWidth <= 0) {
      windowWidth = Math.max(1, maxPixel - minPixel);
    }
    if (windowCenter == null) {
      windowCenter = Math.round((minPixel + maxPixel) / 2);
    }
  } else {
    if (windowWidth == null) windowWidth = 400;
    if (windowCenter == null) windowCenter = 40;
  }

  return {
    rows,
    columns,
    bitsAllocated,
    bitsStored,
    pixelRepresentation,
    samplesPerPixel,
    photometricInterpretation,
    windowCenter,
    windowWidth,
    defaultWindowCenter: windowCenter,
    defaultWindowWidth: windowWidth,
    minPixel: minPixel !== Infinity ? minPixel : 0,
    maxPixel: maxPixel !== -Infinity ? maxPixel : 255,
    rescaleIntercept,
    rescaleSlope,
    patientName,
    patientId,
    patientSex,
    modality,
    studyDescription,
    seriesDescription,
    studyDate,
    instanceNumber,
    sliceLocation,
    pixelArray,
    isEncapsulated,
    jpegBlobUrl,
    allElements,
  };
};

export const renderDicomToCanvas = (dicom, canvas, options = {}) => {
  if (!dicom || !canvas) return;

  const {
    windowWidth = dicom.windowWidth,
    windowCenter = dicom.windowCenter,
    invert = false,
    zoom = 1,
    panX = 0,
    panY = 0,
    rotation = 0,
    flipH = false,
    flipV = false,
  } = options;

  const width = dicom.columns;
  const height = dicom.rows;
  if (!width || !height) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  canvas.width = width;
  canvas.height = height;

  if (dicom.isEncapsulated && dicom.jpegBlobUrl) {
    const img = new Image();
    img.onload = () => {
      ctx.save();
      ctx.clearRect(0, 0, width, height);
      applyCanvasTransforms(ctx, width, height, { zoom, panX, panY, rotation, flipH, flipV });
      ctx.drawImage(img, 0, 0);
      ctx.restore();
    };
    img.src = dicom.jpegBlobUrl;
    return;
  }

  if (!dicom.pixelArray) return;

  const imgData = ctx.createImageData(width, height);
  const data = imgData.data;
  const pixels = dicom.pixelArray;
  const numPixels = Math.min(pixels.length, width * height);

  const ww = Math.max(1, windowWidth);
  const wc = windowCenter;
  const low = wc - ww / 2;
  const high = wc + ww / 2;

  const isMonochrome1 = dicom.photometricInterpretation === "MONOCHROME1";
  const shouldInvert = isMonochrome1 ? !invert : invert;

  for (let i = 0; i < numPixels; i++) {
    const val = pixels[i];
    let intensity;
    if (val <= low) {
      intensity = 0;
    } else if (val >= high) {
      intensity = 255;
    } else {
      intensity = Math.round(((val - low) / ww) * 255);
    }

    if (shouldInvert) {
      intensity = 255 - intensity;
    }

    const idx = i * 4;
    data[idx] = intensity;
    data[idx + 1] = intensity;
    data[idx + 2] = intensity;
    data[idx + 3] = 255;
  }

  if (rotation === 0 && !flipH && !flipV && zoom === 1 && panX === 0 && panY === 0) {
    ctx.putImageData(imgData, 0, 0);
  } else {
    const offscreen = document.createElement("canvas");
    offscreen.width = width;
    offscreen.height = height;
    offscreen.getContext("2d").putImageData(imgData, 0, 0);

    ctx.save();
    ctx.clearRect(0, 0, width, height);
    applyCanvasTransforms(ctx, width, height, { zoom, panX, panY, rotation, flipH, flipV });
    ctx.drawImage(offscreen, 0, 0);
    ctx.restore();
  }
};

const applyCanvasTransforms = (ctx, width, height, { zoom = 1, panX = 0, panY = 0, rotation = 0, flipH = false, flipV = false }) => {
  ctx.translate(width / 2 + panX, height / 2 + panY);
  ctx.scale(zoom, zoom);
  if (rotation !== 0) {
    ctx.rotate((rotation * Math.PI) / 180);
  }
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  ctx.translate(-width / 2, -height / 2);
};

export const createSliceThumbnail = (dicom, size = 64) => {
  if (!dicom || !dicom.rows || !dicom.columns) return null;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const fullCanvas = document.createElement("canvas");
  renderDicomToCanvas(dicom, fullCanvas);

  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.drawImage(fullCanvas, 0, 0, size, size);
    return canvas.toDataURL("image/jpeg", 0.7);
  }
  return null;
};

export const extractDicomFromZip = async (zipArrayBuffer) => {
  const uint8 = zipArrayBuffer instanceof Uint8Array ? zipArrayBuffer : new Uint8Array(zipArrayBuffer);
  const unzipped = fflate.unzipSync(uint8);

  const files = Object.keys(unzipped);
  const dicomSlices = [];

  for (const fileName of files) {
    if (fileName.endsWith("/") || fileName.startsWith("__MACOSX/") || fileName.endsWith(".DS_Store")) {
      continue;
    }

    const fileBytes = unzipped[fileName];
    if (fileBytes.length < 132) continue;

    const isDcmExt = fileName.toLowerCase().endsWith(".dcm") || fileName.toLowerCase().endsWith(".dicom");
    const magic = String.fromCharCode(fileBytes[128], fileBytes[129], fileBytes[130], fileBytes[131]);
    const hasDicomMagic = magic === "DICM";

    if (isDcmExt || hasDicomMagic) {
      try {
        const parsed = parseDicom(fileBytes);
        if (parsed.rows > 0 && parsed.columns > 0) {
          dicomSlices.push({
            id: `slice-${dicomSlices.length + 1}`,
            fileName,
            fileSize: fileBytes.length,
            dicom: parsed,
            thumbnail: createSliceThumbnail(parsed, 64),
          });
        }
      } catch (err) {
        console.warn(`Failed to parse DICOM slice ${fileName}:`, err);
      }
    }
  }

  dicomSlices.sort((a, b) => {
    if (a.dicom.instanceNumber != null && b.dicom.instanceNumber != null && a.dicom.instanceNumber !== b.dicom.instanceNumber) {
      return a.dicom.instanceNumber - b.dicom.instanceNumber;
    }
    if (a.dicom.sliceLocation != null && b.dicom.sliceLocation != null) {
      return a.dicom.sliceLocation - b.dicom.sliceLocation;
    }
    return a.fileName.localeCompare(b.fileName, undefined, { numeric: true });
  });

  return dicomSlices;
};
